import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  addFixedAsset,
  cancelFixedAsset,
  fetchFixedAssetDetail,
  fetchFixedAssets,
  updateFixedAsset,
} from "../../actions/fixedAssetAction";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import AkasSelect from "../../components/AkasSelect";
import { formatIDR } from "../../utils/helpers/formatIDR";
import { hasAnyPermission } from "../../utils/helpers/permissions";
import "../CashBankPage/cash-bank.css";
import DetailModal from "./components/DetailModal";
import FormModal from "./components/FormModal";
import { STATUS_META, formatDate, localDate } from "./constants";
import "./fixed-asset.css";

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const initialFilters = { start_date: startOfYear(), end_date: localDate(), category_id: "", status: "", search: "" };

const AssetGlyph = ({ type = "asset" }) => type === "value" ? (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 7h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z" /><path d="m4 7 3-4h10l3 4M8 12h8M8 16h5" /></svg>
) : type === "depreciation" ? (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 19V5M4 19h16" /><path d="m7 8 4 4 3-3 4 5" /><path d="M18 10v4h-4" /></svg>
) : (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 8h14v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8Z" /><path d="m7 8 2-5h6l2 5M9 13h6M12 10v6" /></svg>
);

const ActionIcon = ({ type }) => type === "detail" ? (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden="true"><path d="M13 5h8M13 9h5M13 15h8M13 19h5" /><path d="M3 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5M3 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4" /></svg>
) : type === "edit" ? (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1l1-4z" /></svg>
) : (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
);

export const FixedAssetPage = (props) => {
  const {
    data,
    loading,
    actionLoading,
    error,
    userProfile,
    fetchFixedAssets,
    fetchFixedAssetDetail,
    addFixedAsset,
    updateFixedAsset,
    cancelFixedAsset,
  } = props;
  const [filters, setFilters] = useState(initialFilters);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [formAsset, setFormAsset] = useState(undefined);
  const [detailAsset, setDetailAsset] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(null);

  const categories = useMemo(() => data?.categories || [], [data]);
  const accounts = useMemo(() => data?.accounts || [], [data]);
  const suppliers = useMemo(() => data?.suppliers || [], [data]);
  const pagination = data?.assets || {};
  const assets = pagination?.data || [];
  const summary = data?.summary || {};
  const canCreate = hasAnyPermission(userProfile, ["create-fixed-assets"]);
  const canEdit = hasAnyPermission(userProfile, ["edit-fixed-assets"]);
  const canCancel = hasAnyPermission(userProfile, ["delete-fixed-assets"]);

  const load = useCallback(() => fetchFixedAssets({
    ...filters,
    category_id: filters.category_id || undefined,
    status: filters.status || undefined,
    search: filters.search || undefined,
    page: currentPage,
    limit: perPage,
  }), [currentPage, fetchFixedAssets, filters, perPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setFilters((current) => ({ ...current, search: searchInput.trim() }));
    }, 320);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const updateFilter = (key, value) => {
    setCurrentPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };
  const saveAsset = async (payload) => {
    const result = formAsset?.id
      ? await updateFixedAsset(formAsset.id, payload)
      : await addFixedAsset(payload);
    if (result) {
      setFormAsset(undefined);
      await load();
    }
  };
  const openDetail = async (asset) => {
    const result = await fetchFixedAssetDetail(asset.id);
    if (result) setDetailAsset(result);
  };
  const confirmCancel = async (_id, reason) => {
    if (!pendingCancel) return false;
    const result = await cancelFixedAsset(pendingCancel.id, reason);
    if (!result) return false;
    setPendingCancel(null);
    if (detailAsset?.id === result.id) setDetailAsset(result);
    await load();
    return true;
  };

  return (
    <div className="page-wrapper cash-bank-page fixed-asset-page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center g-3">
            <div className="col">
              <div className="page-pretitle">KEKAYAAN USAHA</div>
              <h1 className="page-title">Aset & Penyusutan</h1>
              <p className="text-secondary mb-0">Pisahkan belanja aset dari biaya harian, lalu pantau nilai bukunya secara otomatis.</p>
            </div>
            <div className="col-auto">
              {canCreate ? <button type="button" className="btn btn-primary" onClick={() => setFormAsset(null)}>+ Tambah aset</button> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <section className="fixed-asset-filter-card" aria-label="Filter aset">
            <div className="fixed-asset-filter-copy"><strong>Periode perolehan</strong><span>Nilai buku dan penyusutan hanya memakai jadwal aset yang aktif.</span></div>
            <div className="fixed-asset-filter-grid">
              <label><span>Dari</span><input type="date" className="form-control" value={filters.start_date} max={filters.end_date} onChange={(event) => updateFilter("start_date", event.target.value)} /></label>
              <label><span>Sampai</span><input type="date" className="form-control" value={filters.end_date} min={filters.start_date} onChange={(event) => updateFilter("end_date", event.target.value)} /></label>
              <label><span>Kategori</span><AkasSelect className="form-select" value={filters.category_id} onChange={(event) => updateFilter("category_id", event.target.value)}><option value="">Semua kategori</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</AkasSelect></label>
              <label><span>Status</span><AkasSelect className="form-select" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}><option value="">Semua status</option><option value="active">Aktif</option><option value="cancelled">Dibatalkan</option></AkasSelect></label>
              <label className="fixed-asset-search"><span>Pencarian</span><div className="input-icon"><span className="input-icon-addon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg></span><input type="search" className="form-control" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Nomor, nama, lokasi..." /></div></label>
            </div>
          </section>

          {error ? <div className="alert alert-danger">{error}</div> : null}

          <section className="cash-bank-summary-grid fixed-asset-summary-grid" aria-label="Ringkasan aset">
            <article className="cash-bank-summary-card is-primary akas-summary-card"><span className="cash-bank-summary-icon"><AssetGlyph type="value" /></span><div><small>Nilai perolehan</small><strong>{loading && !data ? "..." : formatIDR(summary.acquisition_cost || 0)}</strong><p>{Number(summary.active_count || 0).toLocaleString("id-ID")} aset aktif</p></div></article>
            <article className="cash-bank-summary-card akas-summary-card"><span className="cash-bank-summary-icon"><AssetGlyph /></span><div><small>Nilai buku</small><strong>{formatIDR(summary.book_value || 0)}</strong><p>Setelah akumulasi {formatIDR(summary.accumulated_depreciation || 0)}</p></div></article>
            <article className="cash-bank-summary-card akas-summary-card"><span className="cash-bank-summary-icon"><AssetGlyph type="depreciation" /></span><div><small>Penyusutan periode</small><strong>{formatIDR(summary.period_depreciation || 0)}</strong><p>Beban yang mengurangi laba bersih</p></div></article>
          </section>

          <section className="card fixed-asset-table-card">
            <div className="card-header"><div><h3 className="card-title mb-1">Daftar aset</h3><p className="text-secondary mb-0">Peralatan dan kekayaan usaha yang mempunyai masa manfaat.</p></div><span className="fixed-asset-record-count">{pagination.total || 0} data</span></div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table fixed-asset-table">
                <thead><tr><th>No.</th><th>Aset</th><th>Kategori</th><th>Perolehan</th><th className="text-end">Harga</th><th className="text-end">Akumulasi</th><th className="text-end">Nilai buku</th><th>Status</th><th className="text-end">Aksi</th></tr></thead>
                <tbody>
                  {loading && !assets.length ? Array.from({ length: 5 }).map((_, index) => <tr key={`asset-skeleton-${index}`} className="placeholder-glow">{Array.from({ length: 9 }).map((__, cell) => <td key={cell}><span className="placeholder col-8" /></td>)}</tr>) : null}
                  {!loading && !assets.length ? <tr><td colSpan="9" className="text-center py-5"><div className="fixed-asset-empty"><span><AssetGlyph /></span><strong>Belum ada aset tercatat</strong><p>Catat mesin, perangkat POS, furnitur, dan aset berumur panjang lainnya.</p>{canCreate ? <button type="button" className="btn btn-primary btn-sm" onClick={() => setFormAsset(null)}>Tambah aset pertama</button> : null}</div></td></tr> : null}
                  {assets.map((asset, index) => {
                    const status = STATUS_META[asset.status] || { label: asset.status, className: "bg-secondary-lt" };
                    const cancellable = asset.status === "active" && Number(asset.accumulated_depreciation || 0) === 0;
                    return (
                      <tr key={asset.id} className={asset.status === "cancelled" ? "is-cancelled" : ""}>
                        <td className="text-secondary">{Number(pagination.from || 1) + index}</td>
                        <td><button type="button" className="fixed-asset-name" onClick={() => openDetail(asset)}><span className="fixed-asset-list-icon"><AssetGlyph /></span><span><strong>{asset.name}</strong><small>{asset.asset_no} · {asset.location || "Lokasi belum dicatat"}</small></span></button></td>
                        <td><span className="fixed-asset-category"><i />{asset.category_name}</span></td>
                        <td><strong className="fixed-asset-date">{formatDate(asset.acquired_at)}</strong><small className="d-block text-secondary">Dipakai {formatDate(asset.available_for_use_at)}</small></td>
                        <td className="text-end fw-bold">{formatIDR(asset.acquisition_cost)}</td>
                        <td className="text-end text-secondary">{formatIDR(asset.accumulated_depreciation)}</td>
                        <td className="text-end"><strong className="fixed-asset-book-value">{formatIDR(asset.book_value)}</strong></td>
                        <td><span className={`badge ${status.className}`}>{status.label}</span></td>
                        <td className="text-end">
                          <div className="btn-list flex-nowrap justify-content-end">
                            <button type="button" className="btn btn-1 bg-secondary-lt" onClick={() => openDetail(asset)} aria-label={`Lihat detail ${asset.asset_no}`}>
                              <ActionIcon type="detail" />
                              Detail
                            </button>
                            {canEdit && asset.status === "active" ? (
                              <button type="button" className="btn btn-1 bg-primary-lt" onClick={() => setFormAsset(asset)} aria-label={`Edit ${asset.asset_no}`}>
                                <ActionIcon type="edit" />
                                Edit
                              </button>
                            ) : null}
                            {canCancel && cancellable ? (
                              <button type="button" className="btn btn-1 bg-danger-lt" data-bs-toggle="modal" data-bs-target="#modal-cancel-fixed-asset" onClick={() => setPendingCancel(asset)} aria-label={`Batalkan ${asset.asset_no}`}>
                                <ActionIcon type="cancel" />
                                Batalkan
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="card-footer fixed-asset-pagination">
              <div className="dropdown"><button type="button" className="btn dropdown-toggle" data-bs-toggle="dropdown">{perPage} data</button><div className="dropdown-menu">{[10, 20, 50, 100].map((value) => <button key={value} type="button" className="dropdown-item" onClick={() => { setPerPage(value); setCurrentPage(1); }}>{value} data</button>)}</div></div>
              <span className="text-secondary">Menampilkan {pagination.from || 0}–{pagination.to || 0} dari {pagination.total || 0}</span>
              <ul className="pagination m-0 ms-auto"><li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}><button type="button" className="page-link" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} aria-label="Halaman sebelumnya">‹</button></li>{(pagination.links || []).slice(1, -1).map((item, index) => { const page = item.url ? Number(new URL(item.url).searchParams.get("page")) : null; return <li key={`${item.label}-${index}`} className={`page-item ${item.active ? "active" : ""}`}><button type="button" className="page-link" disabled={!page} onClick={() => page && setCurrentPage(page)}>{item.label}</button></li>; })}<li className={`page-item ${currentPage >= Number(pagination.last_page || 1) ? "disabled" : ""}`}><button type="button" className="page-link" onClick={() => setCurrentPage((page) => Math.min(Number(pagination.last_page || 1), page + 1))} aria-label="Halaman berikutnya">›</button></li></ul>
            </div>
          </section>
        </div>
      </div>

      {formAsset !== undefined ? <FormModal key={formAsset?.id || "create"} asset={formAsset} categories={categories} accounts={accounts} suppliers={suppliers} saving={actionLoading} onClose={() => setFormAsset(undefined)} onSave={saveAsset} /> : null}
      {detailAsset ? <DetailModal asset={detailAsset} onClose={() => setDetailAsset(null)} /> : null}
      <AlertConfirmModal
        type="error"
        title="Batalkan pencatatan aset?"
        message={pendingCancel ? `${pendingCancel.asset_no} · ${pendingCancel.name} senilai ${formatIDR(pendingCancel.acquisition_cost)}. Saldo akan dikembalikan ke ${pendingCancel.account_name}.` : "Saldo Kas & Bank akan dikembalikan melalui reversal."}
        confirmText="Batalkan aset"
        cancelText="Kembali"
        requireReason
        reasonLabel="Alasan pembatalan"
        reasonPlaceholder="Contoh: pencatatan ganda atau akun pembayaran salah..."
        reasonHint="Aset hanya dapat dibatalkan sebelum penyusutan pertama diakui."
        waitForConfirm
        idModal="cancel-fixed-asset"
        idData={pendingCancel?.id}
        onConfirm={confirmCancel}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state.fixedAssets?.data,
  detail: state.fixedAssets?.detail,
  loading: state.fixedAssets?.loading,
  actionLoading: state.fixedAssets?.actionLoading,
  error: state.fixedAssets?.error,
  userProfile: state.auth?.login?.profile,
});

export default connect(mapStateToProps, {
  fetchFixedAssets,
  fetchFixedAssetDetail,
  addFixedAsset,
  updateFixedAsset,
  cancelFixedAsset,
})(FixedAssetPage);
